import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    Injector,
    InputSignal,
    LOCALE_ID,
    OutputEmitterRef,
    Signal,
    WritableSignal,
    afterNextRender,
    effect,
    inject,
    input,
    output,
    runInInjectionContext,
    signal,
    viewChild,
} from '@angular/core';
import { FieldTree, FormField, SchemaPathTree, disabled, email, form, required } from '@angular/forms/signals';

import { BlockDirective, ConcatClassesPipe, ElemDirective } from '@rt-tools/core';
import { NotificationBus, RtButtonDirective, RtFieldComponent, RtInputComponent, RtTextareaComponent } from '@rt-tools/ui-kit-v2';

import { TurnstileWidgetService } from '../turnstile/turnstile-widget.service';
import { feedbackFormConfig } from './feedback-form.config';
import { ESubmissionStatus, IFeedbackFormModel, IFeedbackSubmitPayload } from './feedback-form.types';

const TURNSTILE_WIDGET_WIDTH: number = 300;
const TURNSTILE_WIDGET_HEIGHT: number = 65;

const INITIAL_MODEL: IFeedbackFormModel = {
    email: '',
    message: '',
    captchaToken: '',
};

const BEM_BLOCK: string = 'sc-feedback-form';

@Component({
    selector: 'sc-feedback-form',
    templateUrl: './feedback-form.component.html',
    styleUrl: './feedback-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // forms
        FormField,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ConcatClassesPipe,

        // rt-tools ui-kit
        RtButtonDirective,
        RtFieldComponent,
        RtInputComponent,
        RtTextareaComponent,
    ],
    providers: [TurnstileWidgetService],
    host: {
        class: BEM_BLOCK,
        '(focusin)': 'onFocusIn()',
    },
})
export class FeedbackFormComponent {
    readonly #injector: Injector = inject(Injector);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #turnstile: TurnstileWidgetService = inject(TurnstileWidgetService);
    readonly #notifications: NotificationBus = inject(NotificationBus);

    /**
     * Watches the captcha container's width to rescale the widget:
     * Turnstile renders at a fixed ~300px width, which overflows in a narrow
     * sidebar. Disconnected on component destroy and on `sendAnother()`.
     */
    #resizeObserver: ResizeObserver | null = null;

    protected readonly SubmissionStatus: typeof ESubmissionStatus = ESubmissionStatus;

    protected readonly captchaContainer: Signal<ElementRef<HTMLElement> | undefined> =
        viewChild<ElementRef<HTMLElement>>('captchaContainer');

    protected readonly model: WritableSignal<IFeedbackFormModel> = signal<IFeedbackFormModel>({ ...INITIAL_MODEL });
    protected form!: FieldTree<IFeedbackFormModel>;

    protected readonly status: WritableSignal<ESubmissionStatus> = signal<ESubmissionStatus>(ESubmissionStatus.Idle);

    /**
     * Tracks the "first focus within the form" moment for analytics: we want
     * `feedback_open` to fire once on real user intent, not on every focus
     * event and not on bare component mount (the form is always in DOM).
     */
    #openedEmitted: boolean = false;

    public readonly contextData: InputSignal<unknown> = input<unknown>(null);
    public readonly feedbackOpened: OutputEmitterRef<void> = output();
    public readonly feedbackSubmitted: OutputEmitterRef<void> = output();
    public readonly feedbackFailed: OutputEmitterRef<{ reason: string }> = output<{ reason: string }>();

    constructor() {
        runInInjectionContext(this.#injector, (): void => {
            this.form = form(this.model, (schema: SchemaPathTree<IFeedbackFormModel>): void => {
                required(schema.email);
                email(schema.email);
                required(schema.message);
                required(schema.captchaToken);

                const whileSubmitting: () => boolean = (): boolean => this.status() === ESubmissionStatus.Submitting;
                disabled(schema.email, { when: whileSubmitting });
                disabled(schema.message, { when: whileSubmitting });
            });
        });

        // Schedule deferred Turnstile loading once the form's DOM is rendered
        // (otherwise `captchaContainer()` is undefined and the observer has nothing to watch).
        afterNextRender((): void => {
            const container: HTMLElement | undefined = this.captchaContainer()?.nativeElement;
            if (container) {
                this.#turnstile.scheduleLoad({
                    container,
                    siteKey: feedbackFormConfig.turnstileSiteKey,
                    localeId: this.#localeId,
                    onToken: (token: string): void => {
                        this.#setCaptchaToken(token);
                    },
                    onRendered: (el: HTMLElement): void => {
                        this.#observeContainerWidth(el);
                    },
                });
            }
        });

        // Re-render the widget after `sendAnother()`: form is back to Idle, the
        // container exists, the Turnstile script is already loaded, but the widget
        // reference has been forgotten — render into the fresh container again.
        effect((): void => {
            const container: ElementRef<HTMLElement> | undefined = this.captchaContainer();
            if (container && !this.#turnstile.isRendered && this.#turnstile.hasRequested) {
                this.#turnstile.rerender(container.nativeElement);
            }
        });

        this.#destroyRef.onDestroy((): void => {
            this.#resizeObserver?.disconnect();
        });
    }

    protected async onSubmit(event: Event): Promise<void> {
        event.preventDefault();
        if (this.form().invalid() || this.status() === ESubmissionStatus.Submitting) {
            return;
        }

        this.status.set(ESubmissionStatus.Submitting);

        const current: IFeedbackFormModel = this.model();
        const payload: IFeedbackSubmitPayload = {
            email: current.email.trim(),
            message: current.message.trim(),
            'cf-turnstile-response': current.captchaToken,
            subject: 'GoldenCalc feedback',
            from_name: 'GoldenCalc',
        };

        const ctx: unknown = this.contextData();
        if (ctx !== null && ctx !== undefined) {
            payload.config_json = JSON.stringify(ctx, null, 2);
        }

        // Submit handler runs only in the browser (clicks don't fire on the
        // server), so location is safe to read directly. Captures the full URL
        // including any share-config query params so support can reproduce the
        // user's exact view.
        const href: string | undefined = (globalThis as { location?: Location }).location?.href;
        if (typeof href === 'string' && href.length > 0) {
            payload.share_url = href;
        }

        try {
            const response: Response = await fetch(feedbackFormConfig.feedbackEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(payload),
            });
            const result: { success?: boolean; message?: string } = (await response.json()) as { success?: boolean; message?: string };

            if (result.success === true) {
                this.status.set(ESubmissionStatus.Success);
                this.#resetForm();
                this.feedbackSubmitted.emit();
            } else {
                this.status.set(ESubmissionStatus.Error);
                const reason: string = result.message ?? 'server_error';
                this.#notifications.error(result.message ?? $localize`:@@feedbackError:Не удалось отправить. Попробуйте позже.`);
                this.#turnstile.reset();
                this.feedbackFailed.emit({ reason });
            }
        } catch {
            this.status.set(ESubmissionStatus.Error);
            this.#notifications.error($localize`:@@feedbackError:Не удалось отправить. Попробуйте позже.`);
            this.#turnstile.reset();
            this.feedbackFailed.emit({ reason: 'network_error' });
        }
    }

    protected onFocusIn(): void {
        if (this.#openedEmitted) {
            return;
        }
        this.#openedEmitted = true;
        this.feedbackOpened.emit();
    }

    /**
     * Return the form to `Idle` after a successful submit (the template
     * shows the "thanks" + "send another" CTA in the success branch).
     *
     * Forgets the Turnstile widget and disconnects `#resizeObserver`: the
     * previous `<div #captchaContainer>` is destroyed by the template switch,
     * so the old references are stale. A new container appears, the
     * constructor's `effect()` calls `TurnstileWidgetService.rerender()`. The
     * loaded script is reused — no re-fetch of `api.js`.
     */
    protected sendAnother(): void {
        this.#turnstile.forgetWidget();
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
        this.status.set(ESubmissionStatus.Idle);
    }

    /**
     * Rescales the Turnstile widget to the container's width via
     * CSS transform. Turnstile renders its iframe at a fixed ~300px width;
     * in a narrower sidebar (e.g. 280px) the widget overflows — we solve
     * it with `scale(ratio)` and trim the container height in sync so
     * there is no empty space below.
     *
     * Recomputed on every container width change (`ResizeObserver`).
     */
    #observeContainerWidth(container: HTMLElement): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const applyScale: () => void = (): void => {
            const wrapper: HTMLElement | null = container.firstElementChild as HTMLElement | null;
            if (!wrapper) {
                return;
            }
            const scale: number = Math.min(1, container.clientWidth / TURNSTILE_WIDGET_WIDTH);
            wrapper.style.transformOrigin = 'top left';
            wrapper.style.transform = `scale(${String(scale)})`;
            container.style.height = `${String(TURNSTILE_WIDGET_HEIGHT * scale)}px`;
        };
        applyScale();
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = new ResizeObserver(applyScale);
        this.#resizeObserver.observe(container);
    }

    /**
     * Updates `captchaToken` in the form model. An empty string re-triggers
     * the `required(schema.captchaToken)` validator so the form goes invalid.
     */
    #setCaptchaToken(token: string): void {
        this.model.update((m: IFeedbackFormModel) => ({ ...m, captchaToken: token }));
    }

    /**
     * Full reset of the form to `INITIAL_MODEL` after a successful submit.
     * Order matters: reset the captcha first, then the model signal, then
     * the signal-form state so validators re-run against a clean model.
     */
    #resetForm(): void {
        this.#turnstile.reset();
        this.model.set({ ...INITIAL_MODEL });
        this.form().reset(this.model());
    }
}
